/**
 * YUMIX — generateGradeShareCard
 *
 * Builds a 1080x1920 PNG (Instagram/Facebook Story format) from the
 * Day-7 bilan data, with YUMIX branding + grade + stats + CTA.
 *
 * Pure canvas — no external dependency. Returns a Blob for Web Share API
 * or a fallback download URL.
 */

const FONT_HEAD = "'Barlow Condensed', system-ui, sans-serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const drawRoundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
};

export async function generateGradeShareCard({
  userName = 'Membre',
  gradeLabel = '⭐ Recrue',
  stats = {},
  language = 'fr',
} = {}) {
  const fr = (language || 'fr').startsWith('fr');
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background — deep gradient
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#0a0a0f');
  bg.addColorStop(0.5, '#1a0d05');
  bg.addColorStop(1, '#0a0a0f');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Ambient orange glow
  const glow = ctx.createRadialGradient(W/2, H*0.42, 50, W/2, H*0.42, 700);
  glow.addColorStop(0, 'rgba(249,115,22,0.32)');
  glow.addColorStop(1, 'rgba(249,115,22,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // YUMIX brand mark (top)
  ctx.font = `900 78px ${FONT_HEAD}`;
  ctx.fillStyle = '#f97316';
  ctx.textAlign = 'center';
  ctx.fillText('YUMIX', W/2, 200);

  ctx.font = `600 28px ${FONT_BODY}`;
  ctx.fillStyle = '#888';
  ctx.fillText(fr ? 'COACH NUTRITION + PERFORMANCE' : 'NUTRITION + PERFORMANCE COACH', W/2, 250);

  // Hero flame emoji
  ctx.font = `220px ${FONT_BODY}`;
  ctx.fillText('🔥', W/2, 540);

  // Title
  ctx.font = `900 88px ${FONT_HEAD}`;
  ctx.fillStyle = '#fff';
  ctx.fillText(fr ? 'DÉFI 7 JOURS' : '7-DAY CHALLENGE', W/2, 670);
  ctx.font = `900 88px ${FONT_HEAD}`;
  ctx.fillStyle = '#f97316';
  ctx.fillText(fr ? 'COMPLÉTÉ !' : 'COMPLETE!', W/2, 770);

  // Username
  ctx.font = `600 44px ${FONT_BODY}`;
  ctx.fillStyle = '#bbb';
  ctx.fillText(`— ${userName} —`, W/2, 850);

  // Grade card (gradient orange)
  const cardX = 90, cardY = 950, cardW = W - 180, cardH = 240;
  const grad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
  grad.addColorStop(0, '#f97316');
  grad.addColorStop(1, '#ea580c');
  ctx.fillStyle = grad;
  drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 32);
  ctx.fill();

  ctx.font = `600 32px ${FONT_BODY}`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText(fr ? 'GRADE OFFICIEL' : 'OFFICIAL GRADE', W/2, cardY + 80);

  ctx.font = `900 76px ${FONT_HEAD}`;
  ctx.fillStyle = '#fff';
  ctx.fillText(gradeLabel, W/2, cardY + 175);

  // Stats row (3 columns)
  const sy = 1290;
  const tiles = [
    { v: String(stats.active_days || 0), l: fr ? 'JOURS ACTIFS' : 'ACTIVE DAYS' },
    { v: String(stats.meals_logged || 0), l: fr ? 'REPAS LOGUÉS' : 'MEALS LOGGED' },
    { v: `${stats.consistency_pct || 0}%`,  l: fr ? 'CONSTANCE' : 'CONSISTENCY' },
  ];
  const tileW = 280, gap = 30, totalW = tileW * 3 + gap * 2;
  let tx = (W - totalW) / 2;
  tiles.forEach((t) => {
    ctx.fillStyle = 'rgba(20,20,28,0.7)';
    drawRoundedRect(ctx, tx, sy, tileW, 240, 24);
    ctx.fill();
    ctx.strokeStyle = 'rgba(249,115,22,0.5)';
    ctx.lineWidth = 2;
    drawRoundedRect(ctx, tx, sy, tileW, 240, 24);
    ctx.stroke();

    ctx.font = `900 90px ${FONT_HEAD}`;
    ctx.fillStyle = '#f97316';
    ctx.textAlign = 'center';
    ctx.fillText(t.v, tx + tileW/2, sy + 130);

    ctx.font = `700 26px ${FONT_BODY}`;
    ctx.fillStyle = '#bbb';
    ctx.fillText(t.l, tx + tileW/2, sy + 195);

    tx += tileW + gap;
  });

  // CTA at the bottom
  ctx.font = `600 32px ${FONT_BODY}`;
  ctx.fillStyle = '#aaa';
  ctx.fillText(fr ? 'REJOINS LA MEUTE' : 'JOIN THE PACK', W/2, 1740);

  ctx.font = `900 56px ${FONT_HEAD}`;
  ctx.fillStyle = '#fff';
  ctx.fillText('YUMIX.app', W/2, 1810);

  // Tagline
  ctx.font = `500 24px ${FONT_BODY}`;
  ctx.fillStyle = '#666';
  ctx.fillText(fr
    ? 'Chaque repas. Chaque jour. La meute avance.'
    : 'Every meal. Every day. The pack moves forward.', W/2, 1860);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
  });
}

export async function shareGradeCard(payload) {
  const blob = await generateGradeShareCard(payload);
  if (!blob) return { success: false, reason: 'blob_failed' };
  const file = new File([blob], `yumix-grade-${Date.now()}.png`, { type: 'image/png' });

  // Try Web Share API (mobile, Safari iOS, modern Android)
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'YUMIX — Défi 7 Jours complété',
        text: 'J\'ai complété mon Défi 7 Jours sur YUMIX. Rejoins la meute → yumix.app',
      });
      return { success: true, method: 'web_share' };
    }
  } catch {
    // user cancelled or unsupported — fall through to download
  }

  // Fallback: trigger a download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return { success: true, method: 'download' };
}
